import styled from 'styled-components';

/*
 * Side by side rather than a toggle: the figures and the shape read together, and the
 * accessible view is always present. Below 1200 px they stack.
 */
export const Split = styled('div')`
  display: flex;
  /* Both cards the same height: top-aligned, the table looked truncated. */
  align-items: stretch;
  gap: 16px;

  @media (max-width: 1200px) {
    flex-direction: column;
  }
`;

export const TablePane = styled('div')`
  flex: 0 0 420px;
  min-width: 0;

  @media (max-width: 1200px) {
    flex: 1 1 auto;
    width: 100%;
  }
`;

export const ChartPane = styled('div')`
  /* min-width zero, otherwise the chart refuses to shrink inside a flex container. */
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
`;

/*
 * Light surface over the application's dark background, like the ag-grid tables: without it the
 * chart ink became unreadable and the past band read as its background.
 */
const panel = `
  background: #ffffff;
  border-radius: 4px;
  box-shadow: 0 1px 4px rgb(0 0 0 / 20%);
`;

export const ChartWrapper = styled('div')`
  ${panel}
  width: 100%;
  padding: 16px 16px 8px;

  .recharts-cartesian-axis-tick text {
    fill: ${({ theme }) => theme.dark_gray};
    font-size: 12px;
  }

  .recharts-legend-item-text {
    color: ${({ theme }) => theme.dark_gray} !important;
    font-size: 12px;
  }
`;

export const LegendRow = styled('div')`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 20px;
  justify-content: center;
  padding: 4px 0 2px;
  font-size: 12px;
  color: ${({ theme }) => theme.dark_gray};

  span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  /* The swatch carries the colour; the text stays ink. */
  i {
    width: 12px;
    height: 12px;
    border-radius: 2px;
    display: inline-block;
    flex: none;
  }

  i.hatched {
    border-radius: 2px;
    overflow: hidden;
    line-height: 0;
  }

  i.line {
    height: 0;
    border-top: 2px solid ${({ theme }) => theme.dark_gray};
    border-radius: 0;
  }

  i.line.dashed {
    border-top-style: dashed;
  }
`;

export const TooltipBox = styled('div')`
  background: ${({ theme }) => theme.white};
  border: 1px solid ${({ theme }) => theme.light_soft_gray};
  border-radius: 4px;
  box-shadow: 0 2px 8px rgb(0 0 0 / 15%);
  padding: 10px 12px;
  min-width: 190px;
  color: ${({ theme }) => theme.dark_gray};
  font-size: 12px;

  dl {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 4px 16px;
    margin: 8px 0 0;
  }

  dt {
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: normal;
  }

  dd {
    margin: 0;
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  /* Identity swatch: the colour lives here, never in the text. */
  i {
    width: 10px;
    height: 10px;
    border-radius: 2px;
    display: inline-block;
    flex: none;
  }

  p {
    margin: 8px 0 0;
    color: ${({ theme }) => theme.light_gray};
    font-style: italic;
  }
`;

export const TableWrapper = styled('div')`
  ${panel}
  padding: 16px;
  overflow-x: auto;
  height: 100%;

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    color: ${({ theme }) => theme.dark_gray};
  }

  caption {
    text-align: left;
    padding-bottom: 8px;
    font-weight: 600;
  }

  th,
  td {
    border-bottom: 1px solid ${({ theme }) => theme.light_soft_gray};
    padding: 6px 12px;
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  thead th,
  tbody th {
    text-align: left;
    font-weight: 600;
  }

  /* The current week is the hinge: it deserves to be spotted at a glance. */
  tr.is-current {
    background: ${({ theme }) => theme.pale_yellow}33;
  }

  /* Weeks ahead are projections: italics say so without adding a column. */
  tr.is-future th,
  tr.is-future td {
    font-style: italic;
    color: ${({ theme }) => theme.light_gray};
  }
`;

/* Hairline between the two panes: without it they read as one chart cut in half. */
export const PanelGap = styled('div')`
  height: 10px;
`;
